import React from 'react';
import { DollarSign, TrendingUp, Users, Shield, CheckCircle, Calendar } from 'lucide-react';
import { Card } from './ui/card';

const HowInvestmentWorks = () => {
  const steps = [
    {
      number: "01",
      icon: Users,
      title: "Fan Discovers You",
      description: "Your fan loves your content and wants to support your growth",
      color: "cyan"
    },
    {
      number: "02",
      icon: DollarSign,
      title: "Makes Investment",
      description: "They invest any amount (starting from £10) directly through the platform",
      color: "purple"
    },
    {
      number: "03",
      icon: TrendingUp,
      title: "You Create & Grow",
      description: "Continue creating amazing content while your channel grows",
      color: "pink"
    },
    {
      number: "04",
      icon: Shield,
      title: "Revenue Sharing",
      description: "Investors automatically receive their share of your revenue monthly",
      color: "blue"
    }
  ];

  const examples = [
    {
      creator: "Gaming Creator",
      subscribers: "50K subscribers",
      scenario: "Raises £25,000 from 200 fans",
      investment: "£100",
      returns: "£15/month",
      roi: "15% monthly return",
      details: [
        "Average investment: £125 per fan",
        "Investors own 10% of future revenue",
        "Creator keeps 90% of all earnings",
        "12-month investor agreement"
      ],
      color: "from-cyan-500/20 to-blue-500/20 border-cyan-500/30"
    },
    {
      creator: "Beauty Influencer",
      subscribers: "100K subscribers",
      scenario: "Raises £50,000 from 350 fans",
      investment: "£150",
      returns: "£28/month",
      roi: "18% monthly return",
      details: [
        "Average investment: £142 per fan",
        "Investors own 12% of future revenue",
        "Creator keeps 88% of all earnings",
        "18-month investor agreement"
      ],
      color: "from-pink-500/20 to-purple-500/20 border-pink-500/30"
    },
    {
      creator: "Tech Reviewer",
      subscribers: "200K subscribers",
      scenario: "Raises £100,000 from 500 fans",
      investment: "£200",
      returns: "£45/month",
      roi: "22% monthly return",
      details: [
        "Average investment: £200 per fan",
        "Investors own 15% of future revenue",
        "Creator keeps 85% of all earnings",
        "24-month investor agreement"
      ],
      color: "from-purple-500/20 to-pink-500/20 border-purple-500/30"
    }
  ];

  const colorClasses = {
    cyan: {
      bg: "from-cyan-500/10 to-blue-500/10",
      border: "border-cyan-500/30",
      text: "text-cyan-400",
      glow: "bg-cyan-500/20"
    },
    blue: {
      bg: "from-blue-500/10 to-indigo-500/10",
      border: "border-blue-500/30",
      text: "text-blue-400",
      glow: "bg-blue-500/20"
    },
    purple: {
      bg: "from-purple-500/10 to-pink-500/10",
      border: "border-purple-500/30",
      text: "text-purple-400",
      glow: "bg-purple-500/20"
    },
    pink: {
      bg: "from-pink-500/10 to-red-500/10",
      border: "border-pink-500/30",
      text: "text-pink-400",
      glow: "bg-pink-500/20"
    }
  };

  return (
    <section id="how-investment-works" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a2e] via-[#0a1a3e] to-[#0a0a2e]"></div>
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 space-y-4">
          <span className="px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-pink-500/10 border border-cyan-500/30 text-cyan-400 text-sm font-medium inline-block backdrop-blur-sm">
            Simple & Transparent
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            How Fan Investment
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-500 bg-clip-text text-transparent"> Actually Works</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            A straightforward process that benefits both creators and their biggest supporters
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const colors = colorClasses[step.color];
            
            return (
              <div key={index} className="relative group">
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-20 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-gray-700 to-transparent"></div>
                )}

                <div className={`relative bg-gradient-to-br ${colors.bg} backdrop-blur-sm border ${colors.border} rounded-2xl p-6 h-full transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}>
                  {/* Step Number */}
                  <div className={`absolute -top-4 -right-4 w-12 h-12 rounded-full bg-gradient-to-br ${colors.bg} border ${colors.border} flex items-center justify-center backdrop-blur-sm`}>
                    <span className={`text-lg font-bold ${colors.text}`}>{step.number}</span>
                  </div>

                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${colors.bg} border ${colors.border} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-8 h-8 ${colors.text}`} />
                  </div>

                  {/* Content */}
                  <h3 className={`text-xl font-bold ${colors.text} mb-3`}>
                    {step.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Decorative Glow */}
                  <div className={`absolute -bottom-2 -right-2 w-20 h-20 ${colors.glow} rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Real Examples Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Real-World
              <span className="bg-gradient-to-r from-cyan-400 to-pink-500 bg-clip-text text-transparent"> Examples</span>
            </h3>
            <p className="text-lg text-gray-400">See how creators at different levels benefit from fan investment</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {examples.map((example, index) => (
              <Card 
                key={index}
                className={`group relative bg-gradient-to-br ${example.color} backdrop-blur-sm border p-6 hover:scale-105 transition-all duration-300 hover:shadow-2xl overflow-hidden`}
              >
                {/* Header */}
                <div className="mb-6">
                  <h4 className="text-2xl font-bold text-white mb-2">{example.creator}</h4>
                  <p className="text-gray-400 mb-4">{example.subscribers}</p>
                  <div className="h-px bg-gradient-to-r from-cyan-500/50 to-pink-500/50"></div>
                </div>

                {/* Scenario */}
                <div className="bg-black/30 rounded-xl p-4 mb-6 backdrop-blur-sm">
                  <p className="text-cyan-400 font-bold text-lg mb-2">Scenario:</p>
                  <p className="text-white text-xl font-bold">{example.scenario}</p>
                </div>

                {/* Investment Example */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-black/30 rounded-lg p-4 backdrop-blur-sm">
                    <DollarSign className="w-6 h-6 text-cyan-400 mb-2" />
                    <p className="text-gray-400 text-sm">Fan Invests</p>
                    <p className="text-white text-2xl font-bold">{example.investment}</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-4 backdrop-blur-sm">
                    <TrendingUp className="w-6 h-6 text-pink-400 mb-2" />
                    <p className="text-gray-400 text-sm">Gets Monthly</p>
                    <p className="text-pink-400 text-2xl font-bold">{example.returns}</p>
                  </div>
                </div>

                {/* ROI Badge */}
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-3 mb-6 text-center">
                  <p className="text-green-400 font-bold text-lg">{example.roi}</p>
                </div>

                {/* Details */}
                <div className="space-y-2">
                  {example.details.map((detail, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-300 text-sm">{detail}</p>
                    </div>
                  ))}
                </div>

                {/* Decorative Glow */}
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </Card>
            ))}
          </div>
        </div>

        {/* Key Benefits */}
        <div className="relative p-12 rounded-3xl bg-gradient-to-br from-gray-900/80 to-gray-800/80 border border-gray-700/50 backdrop-blur-sm">
          <h3 className="text-3xl font-bold text-white mb-8 text-center">
            Why This Changes Everything
          </h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: "Protected Investments",
                description: "All investments are secured with legal agreements and tracked on the platform"
              },
              {
                icon: Calendar,
                title: "Flexible Terms",
                description: "Choose investment periods from 12-36 months that work for you and your investors"
              },
              {
                icon: TrendingUp,
                title: "Automatic Payouts",
                description: "Revenue shares are calculated and distributed automatically each month"
              },
              {
                icon: Users,
                title: "Build Community",
                description: "Investors become your biggest supporters and advocates"
              },
              {
                icon: DollarSign,
                title: "Keep Control",
                description: "You retain full creative control - investors support, don't control"
              },
              {
                icon: CheckCircle,
                title: "Win-Win Model",
                description: "When you succeed, everyone succeeds - aligned incentives for growth"
              }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-br from-gray-800/50 to-gray-700/50 border border-gray-600/30 hover:border-cyan-500/30 transition-all group">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-pink-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-1">{benefit.title}</h4>
                    <p className="text-gray-400 text-sm">{benefit.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowInvestmentWorks;
